/**
 * Organization Management Component
 * 
 * Allows developers to manage organizations with full details.
 * Part of the Admin Panel (developer-only).
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { Building2, Plus, Edit2, Trash2, MapPin, Mail, Phone } from 'lucide-react';

// Load Google Maps API (reuse if already loaded)
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const OrganizationManagement = () => {
  const { isDark } = useTheme();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Google Places autocomplete
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'Customer',
    address: '',
    latitude: null,
    longitude: null,
    contactEmail: '',
    contactPhone: '',
    companyCode: '',
    modulesAccess: ['asset_tracking'], // Default to asset tracking only
    imageUrl: null // Organization logo/image URL
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadOrganizations();
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => setMapsLoaded(true))
      .catch(err => console.error('Failed to load Google Maps:', err));
  }, []);

  // Initialize autocomplete when modal opens and maps is loaded
  useEffect(() => {
    if (!showModal || !mapsLoaded || !addressInputRef.current) return;
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    // Clean up previous autocomplete
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      fields: ['formatted_address', 'geometry']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || prev.address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        }));
      }
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [showModal, mapsLoaded]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get('/api/organizations');
      setOrganizations(data || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
      const errorMessage = err.response?.data?.message || err.data?.message || err.message || 'Unknown error';
      setError('Failed to load organizations: ' + errorMessage);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };


  const handleOpenModal = (org = null) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        name: org.name || '',
        type: org.type || 'Customer',
        address: org.address || '',
        latitude: org.latitude || null,
        longitude: org.longitude || null,
        contactEmail: org.contact_email || '',
        contactPhone: org.contact_phone || '',
        companyCode: org.company_code || '',
        modulesAccess: org.modules_access || ['asset_tracking'],
        imageUrl: org.image_url || null
      });
      setImagePreview(org.image_url || null);
    } else {
      setEditingOrg(null);
      setFormData({
        name: '',
        type: 'Customer',
        address: '',
        latitude: null,
        longitude: null,
        contactEmail: '',
        contactPhone: '',
        companyCode: '',
        modulesAccess: ['asset_tracking'],
        imageUrl: null
      });
      setImagePreview(null);
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      type: 'Customer',
      address: '',
      latitude: null,
      longitude: null,
      contactEmail: '',
      contactPhone: '',
      companyCode: '',
      modulesAccess: ['asset_tracking'],
      imageUrl: null
    });
    setImagePreview(null);
    setError('');
    setSuccess('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // If editing existing org, upload immediately
    if (editingOrg) {
      setUploadingImage(true);
      setError('');
      
      try {
        const formData = new FormData();
        formData.append('image', file);

        const result = await api.upload(
          `/api/organizations/${editingOrg.id}/upload-image`,
          formData
        );

        if (result.imageUrl || result.gcsPath) {
          setFormData(prev => ({ ...prev, imageUrl: result.gcsPath || result.imageUrl }));
          setSuccess('Image uploaded successfully');
        }
      } catch (err) {
        console.error('Failed to upload image:', err);
        setError(err.message || 'Failed to upload image');
        setImagePreview(null);
      } finally {
        setUploadingImage(false);
      }
    } else {
      // For new orgs, store file to upload after creation
      setFormData(prev => ({ ...prev, imageFile: file }));
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, imageUrl: null, imageFile: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!formData.type) {
      setError('Organization type is required');
      return false;
    }
    // Company code is optional but if provided, must be valid format
    if (formData.companyCode && !/^[A-Z0-9]{3,20}$/.test(formData.companyCode.toUpperCase().trim())) {
      setError('Company code must be 3-20 alphanumeric characters (uppercase)');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        address: formData.address.trim() || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        contactEmail: formData.contactEmail.trim() || null,
        contactPhone: formData.contactPhone.trim() || null,
        companyCode: formData.companyCode.trim() || null,
        modulesAccess: formData.modulesAccess || ['asset_tracking']
      };

      let orgId;
      if (editingOrg) {
        // Update existing organization
        const result = await api.put(`/api/organizations/${editingOrg.id}`, payload);
        orgId = editingOrg.id;
        setSuccess('Organization updated successfully');
      } else {
        // Create new organization
        const result = await api.post('/api/organizations', payload);
        orgId = result.id;
        setSuccess('Organization created successfully');
      }

      // Upload image if a new file was selected (for new orgs)
      if (!editingOrg && formData.imageFile) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('image', formData.imageFile);
          await api.upload(`/api/organizations/${orgId}/upload-image`, imageFormData);
        } catch (imgErr) {
          console.error('Failed to upload image:', imgErr);
          // Don't fail the whole operation if image upload fails
        }
      }

      // Reload organizations after a short delay
      setTimeout(() => {
        loadOrganizations();
        handleCloseModal();
      }, 1000);
    } catch (err) {
      console.error('Failed to save organization:', err);
      const errorMessage = err.response?.data?.message || err.data?.message || err.message || 'Unknown error';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Are you sure you want to delete "${org.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      await api.delete(`/api/organizations/${org.id}`);
      setSuccess('Organization deleted successfully');
      setTimeout(() => {
        loadOrganizations();
      }, 1000);
    } catch (err) {
      console.error('Failed to delete organization:', err);
      const errorMessage = err.response?.data?.message || err.data?.message || err.message || 'Unknown error';
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'Vendor': return isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-800';
      case 'Customer': return isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800';
      default: return isDark ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`rounded-xl overflow-hidden ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className={`p-6 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Organization Management
            </h2>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Create and manage organizations with full details
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Organization
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className={`mx-6 mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm`}>
          {error}
        </div>
      )}
      {success && (
        <div className={`mx-6 mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm`}>
          {success}
        </div>
      )}

      {/* Organizations Table */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading organizations...</p>
          </div>
        ) : organizations.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 size={48} className={`mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No organizations found. Create your first organization to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Organization
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Company / Code
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Address
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Contact
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Users
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Warehouses
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Modules Access
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {organizations.map((org) => (
                  <tr key={org.id} className={`hover:${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <td className={`px-6 py-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <div className="flex items-center gap-2">
                        {org.image_url ? (
                          <img 
                            src={org.image_url} 
                            alt={org.name}
                            className="w-8 h-8 rounded object-contain flex-shrink-0 bg-gray-100 border border-gray-200"
                            onError={(e) => {
                              // Fallback to building icon if image fails to load
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <Building2 
                          size={18} 
                          className={`${isDark ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0 ${org.image_url ? 'hidden' : ''}`}
                        />
                        <div>
                          <span className="font-medium">{org.name}</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${getTypeBadgeColor(org.type)}`}>
                            {org.type}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div>
                        {org.company_code ? (
                          <code className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-gray-900 text-blue-400' : 'bg-gray-100 text-blue-600'}`}>
                            {org.company_code}
                          </code>
                        ) : (
                          <span className="text-gray-500 text-sm">No code</span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {org.address ? (
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{org.address}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">No address</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="space-y-1">
                        {org.contact_email && (
                          <div className="flex items-center gap-1">
                            <Mail size={12} />
                            <span className="truncate max-w-xs">{org.contact_email}</span>
                          </div>
                        )}
                        {org.contact_phone && (
                          <div className="flex items-center gap-1">
                            <Phone size={12} />
                            <span>{org.contact_phone}</span>
                          </div>
                        )}
                        {!org.contact_email && !org.contact_phone && (
                          <span className="text-gray-500">No contact info</span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{org.user_count || 0}</span>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          user{org.user_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{org.warehouse_count || 0}</span>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          warehouse{org.warehouse_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex flex-wrap gap-1.5">
                        {[...new Set(org.modules_access || ['asset_tracking'])].map((module) => {
                          // Get module display name
                          const getModuleName = (m) => {
                            if (m === 'asset_tracking') return 'Asset Tracking';
                            if (m === 'vehicle_tracking') return 'Vehicle Tracking';
                            if (m === 'workflows') return 'Workflows';
                            return m; // Fallback to raw value
                          };
                          
                          // Get module styling
                          const getModuleStyle = (m) => {
                            if (m === 'asset_tracking') {
                              return isDark
                                ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50'
                                : 'bg-blue-100 text-blue-800 border border-blue-200';
                            }
                            if (m === 'vehicle_tracking') {
                              return isDark
                                ? 'bg-purple-900/30 text-purple-400 border border-purple-800/50'
                                : 'bg-purple-100 text-purple-800 border border-purple-200';
                            }
                            if (m === 'workflows') {
                              return isDark
                                ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/50'
                                : 'bg-emerald-100 text-emerald-800 border border-emerald-200';
                            }
                            // Default fallback
                            return isDark
                              ? 'bg-gray-900/30 text-gray-400 border border-gray-800/50'
                              : 'bg-gray-100 text-gray-800 border border-gray-200';
                          };
                          
                          return (
                            <span
                              key={module}
                              className={`px-2 py-0.5 rounded text-xs font-medium ${getModuleStyle(module)}`}
                            >
                              {getModuleName(module)}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isDark 
                          ? 'bg-green-900/30 text-green-400 border border-green-800/50' 
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(org)}
                          className={`p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(org)}
                          disabled={deleting}
                          className={`p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} transition-colors ${
                            deleting
                              ? 'text-gray-400 cursor-not-allowed'
                              : isDark
                              ? 'text-red-400 hover:text-red-300'
                              : 'text-red-600 hover:text-red-700'
                          }`}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? '' : 'bg-white border-gray-200'}`}>
            <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editingOrg ? 'Edit Organization' : 'Create Organization'}
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {editingOrg ? 'Update organization details' : 'Create a new organization with full details'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {error && (
                <div className={`p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm`}>
                  {error}
                </div>
              )}
              {success && (
                <div className={`p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm`}>
                  {success}
                </div>
              )}

              {/* Organization Logo/Image */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Organization Logo
                </label>
                <div className="flex items-center gap-4">
                  {(imagePreview || formData.imageUrl) && (
                    <div className="relative">
                      <img 
                        src={imagePreview || formData.imageUrl} 
                        alt="Organization logo"
                        className="w-20 h-20 rounded-lg object-cover border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      disabled={uploadingImage}
                      className="hidden"
                      id="org-image-upload"
                    />
                    <label
                      htmlFor="org-image-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                        uploadingImage
                          ? 'opacity-50 cursor-not-allowed'
                          : isDark
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {uploadingImage ? (
                        <>
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Building2 size={16} />
                          <span>{imagePreview || formData.imageUrl ? 'Change Image' : 'Upload Image'}</span>
                        </>
                      )}
                    </label>
                    <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      PNG, JPG up to 10MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Organization Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Acme Corporation"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Organization Type */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="Customer">Customer</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>

              {/* Company Code */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Company Code
                </label>
                <input
                  type="text"
                  value={formData.companyCode}
                  onChange={(e) => handleChange('companyCode', e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={20}
                  className={`w-full px-4 py-3 rounded-lg border font-mono ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Optional - 3-20 alphanumeric characters (uppercase). Users will use this code to sign up.
                </p>
              </div>

              {/* Address with Google Places Autocomplete */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Address
                </label>
                <input
                  ref={addressInputRef}
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Start typing to search..."
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formData.latitude && formData.longitude && (
                  <p className={`mt-1 text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                  </p>
                )}
                {!mapsLoaded && (
                  <p className={`mt-1 text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                    Loading Google Maps...
                  </p>
                )}
                {mapsLoaded && !formData.latitude && (
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Start typing to search for an address
                  </p>
                )}
              </div>

              {/* Contact Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="contact@organization.com"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange('contactPhone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Modules Access */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Modules Access
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isDark
                      ? formData.modulesAccess.includes('asset_tracking')
                        ? 'bg-blue-900/20 border-blue-500/50'
                        : 'bg-gray-900/50 border-gray-700'
                      : formData.modulesAccess.includes('asset_tracking')
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.modulesAccess.includes('asset_tracking')}
                      onChange={(e) => {
                        const newModules = e.target.checked
                          ? [...formData.modulesAccess, 'asset_tracking']
                          : formData.modulesAccess.filter(m => m !== 'asset_tracking');
                        // Ensure asset_tracking is always included
                        if (!newModules.includes('asset_tracking')) {
                          newModules.push('asset_tracking');
                        }
                        handleChange('modulesAccess', newModules);
                      }}
                      disabled={true} // Asset tracking is always required
                      className="w-5 h-5 rounded border-2 border-blue-500 bg-blue-600 text-white focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                    />
                    <div>
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Asset Tracking
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Warehouse & Asset Management (Always enabled)
                      </div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isDark
                      ? formData.modulesAccess.includes('vehicle_tracking')
                        ? 'bg-purple-900/20 border-purple-500/50'
                        : 'bg-gray-900/50 border-gray-700'
                      : formData.modulesAccess.includes('vehicle_tracking')
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.modulesAccess.includes('vehicle_tracking')}
                      onChange={(e) => {
                        const newModules = e.target.checked
                          ? [...formData.modulesAccess, 'vehicle_tracking']
                          : formData.modulesAccess.filter(m => m !== 'vehicle_tracking');
                        handleChange('modulesAccess', newModules);
                      }}
                      className="w-5 h-5 rounded border-2 border-purple-500 bg-purple-600 text-white focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    />
                    <div>
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Vehicle Tracking
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        GPS Vehicle Tracking & Telemetry
                      </div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isDark
                      ? formData.modulesAccess.includes('workflows')
                        ? 'bg-emerald-900/20 border-emerald-500/50'
                        : 'bg-gray-900/50 border-gray-700'
                      : formData.modulesAccess.includes('workflows')
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.modulesAccess.includes('workflows')}
                      onChange={(e) => {
                        const newModules = e.target.checked
                          ? [...formData.modulesAccess, 'workflows']
                          : formData.modulesAccess.filter(m => m !== 'workflows');
                        handleChange('modulesAccess', newModules);
                      }}
                      className="w-5 h-5 rounded border-2 border-emerald-500 bg-emerald-600 text-white focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                    />
                    <div>
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Workflows
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Repair Workflow Management (Vendor View)
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className={`p-6 border-t flex gap-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleCloseModal}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : editingOrg ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
